import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportError } from "@/lib/errorReporting";

/**
 * Top-level React error boundary (backlog ADMIN-9) — wraps <App /> in
 * main.tsx. window.location is read directly rather than a router hook,
 * since componentDidCatch is a plain class method with no access to
 * router context.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    void reportError({
      message: error.message,
      stack: error.stack,
      name: error.name,
      component: info.componentStack?.trim().split("\n")[0] ?? null,
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">
        <p className="font-display text-2xl uppercase tracking-wide">Something went wrong</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          We've been notified. Reloading the page usually fixes it.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-xl bg-[image:var(--gradient-arc)] px-6 py-3 font-display text-lg uppercase tracking-wide text-primary-foreground"
        >
          Reload
        </button>
      </div>
    );
  }
}
